// `GET /auth/memberships`: the contexts a signed-in user may select, by name (#414).
//
// The route answers under **no** selected context, because it is what a fresh
// session calls first, and it lists the caller's memberships and nothing else
// (`routes/auth.py::memberships`, `auth/service.py::Auth.list_memberships`).
// Each row names the workspace or org and the org above it, so the picker can say
// "Acme Retail · Northwind Managed" where it used to ask for a uuid.

import type { Api } from "./http";
import type { ContextChoice, SessionResponse } from "./session";

export const MEMBERSHIPS_PATH = "/auth/memberships";

/** `Membership.public()`. Exactly one of `workspace_id` and `org_id` is set; `kind` says which. */
export interface Membership {
  kind: "workspace" | "org";
  workspace_id: string | null;
  org_id: string | null;
  /** The workspace's name, or the org's for an org membership. */
  name: string | null;
  /** The org above the workspace; the org itself for an org membership. */
  org_name: string | null;
  org_kind: "partner" | "customer" | null;
  role: string;
}

/**
 * What an org is called by its kind (#1187): a Partner is an org of kind `partner`, a Customer
 * one of kind `customer`. Where the kind is not known, either.
 */
export function orgKindName(kind: string | null | undefined): string {
  if (kind === "partner") return "Partner";
  if (kind === "customer") return "Customer";
  return "Partner or customer";
}

export type OrgKind = "partner" | "customer";

/** The part of `GET /api/v1/orgs`'s row (`OrgRow`) these labels read. */
interface OrgNamed {
  org_id: string;
  name: string;
  kind: OrgKind;
}

/**
 * The kind of the org a session reads under, when it reads under an org (#1205): a
 * member's from their membership, a platform viewer's from `GET /api/v1/orgs`, which
 * is read for it in an org context. `null` in a workspace, or where neither holds it.
 */
export function orgKindOf(
  session: SessionResponse,
  memberships: readonly Membership[] | null,
  orgs: readonly OrgNamed[] | null,
): OrgKind | null {
  if (session.workspace_id !== null || session.org_id === null) return null;
  const membership = memberships?.find((m) => m.kind === "org" && m.org_id === session.org_id);
  if (membership?.org_kind) return membership.org_kind;
  return orgs?.find((org) => org.org_id === session.org_id)?.kind ?? null;
}

/**
 * What a page that belongs to a workspace says to an org context (#1205): who the
 * reader is reading as, and where to go to do `what` instead.
 */
export function readingAsOrg(kind: OrgKind | null, what: string): string {
  if (kind === "partner") return `You are reading as a partner. Open a customer's workspace to ${what}.`;
  if (kind === "customer") return `You are reading as a customer. Open one of its workspaces to ${what}.`;
  return `You are reading as a partner or customer. Open a workspace to ${what}.`;
}

export const listMemberships = (api: Api): Promise<Membership[]> =>
  api.get<Membership[]>(MEMBERSHIPS_PATH);

/** The choice `POST /auth/session/context` takes for a membership. */
export function choiceFor(membership: Membership): ContextChoice {
  return membership.kind === "workspace" && membership.workspace_id !== null
    ? { workspace_id: membership.workspace_id }
    : { org_id: membership.org_id ?? "" };
}

/** The membership a session is reading under, if the list holds it. */
export function currentMembership(
  session: SessionResponse,
  memberships: Membership[] | null,
): Membership | null {
  if (memberships === null) return null;
  return (
    memberships.find((m) =>
      session.workspace_id !== null
        ? m.workspace_id === session.workspace_id
        : session.org_id !== null && m.kind === "org" && m.org_id === session.org_id,
    ) ?? null
  );
}

/**
 * What the rail calls the context: its name, or the id when the list does not name it.
 * An org context a platform admin is viewing is named from the platform's org list (#1205).
 */
export function contextName(
  session: SessionResponse,
  memberships: Membership[] | null,
  orgs: readonly OrgNamed[] | null = null,
): string {
  const current = currentMembership(session, memberships);
  if (current?.name) return current.name;
  if (session.workspace_id !== null) return `Workspace ${session.workspace_id}`;
  if (session.org_id !== null) {
    const listed = orgs?.find((org) => org.org_id === session.org_id);
    if (listed?.name) return listed.name;
    return `${orgKindName(orgKindOf(session, memberships, orgs))} ${session.org_id}`;
  }
  return "No workspace";
}

/** The org a visiting session acts for, by name, when the list holds it (#419). */
export function actingOrgName(
  session: SessionResponse,
  memberships: Membership[] | null,
): string | null {
  const acting = session.acting_org_id ?? null;
  if (acting === null) return null;
  const org = memberships?.find((m) => m.kind === "org" && m.org_id === acting);
  return org?.name ?? `partner ${acting}`;
}
